import { S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { getAccessTokenFromStorage } from "../services/apis/auth";

// API Gateway base URL for STS credentials
// Use proxy in development to avoid CORS issues
const API_GATEWAY_BASE_URL = process.env.NODE_ENV === 'development'
  ? '/api-gateway'  // proxy path
  : 'https://0dk5a0h5wf.execute-api.ap-northeast-2.amazonaws.com';

/**
 * Issue token response structure
 * @typedef {Object} IssueTokenResponse
 * @property {Object} credentials
 * @property {string} credentials.accessKeyId
 * @property {string} credentials.secretAccessKey
 * @property {string} credentials.sessionToken
 * @property {string} credentials.expiration
 * @property {string} bucket
 * @property {string} region
 * @property {string} keyPrefix
 */

/**
 * Fetch STS temporary credentials from backend
 * @param {Object} params - Request parameters
 * @param {number} params.school_id - School ID
 * @param {number} params.studio_id - Studio ID
 * @param {number} params.scene_id - Scene ID
 * @returns {Promise<IssueTokenResponse>}
 */
async function fetchToken({ school_id, studio_id, scene_id }) {
  const res = await fetch(`${API_GATEWAY_BASE_URL}/photo/sts-credentials`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAccessTokenFromStorage()}`,
    },
    body: JSON.stringify({
      school_id,
      studio_id,
      scene_id
    }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    console.error(`Failed to issue token: ${res.status} ${res.statusText}`, errorText);
    throw new Error(`Failed to issue token: ${res.status} ${res.statusText}`);
  }

  const data = await res.json();
  console.log("STS Token Response:", data);

  // Validate response structure
  if (!data.access_key_id || !data.secret_access_key || !data.session_token ||
      !data.bucket || !data.region || !data.upload_prefix) {
    console.error("Invalid token response structure:", data);
    throw new Error("Invalid token response: missing required fields");
  }

  // Map snake_case response to camelCase format
  return {
    credentials: {
      accessKeyId: data.access_key_id,
      secretAccessKey: data.secret_access_key,
      sessionToken: data.session_token,
      expiration: data.expiration
    },
    bucket: data.bucket,
    region: data.region,
    keyPrefix: data.upload_prefix
  };
}

/**
 * Upload multiple files to S3 using STS temporary credentials
 * @param {File[]} files - Array of files to upload
 * @param {Object} options - Upload options
 * @param {number} options.school_id - School ID
 * @param {number} options.studio_id - Studio ID
 * @param {number} options.scene_id - Scene ID
 * @param {Function} options.onProgress - Progress callback (fileIndex, percentage)
 * @param {Function} options.onFileComplete - Callback when a file upload completes (fileIndex, key)
 * @param {number} options.concurrency - Number of concurrent uploads (default: 16)
 * @returns {Promise<Array<{key: string, size: number, etag: string}>>} - Array of uploaded file metadata
 */
export async function uploadFilesToS3(files, options = {}) {
  const {
    school_id,
    studio_id,
    scene_id,
    onProgress = () => {},
    onFileComplete = () => {},
    concurrency = 8,
  } = options;

  // Fetch STS credentials from backend
  const token = await fetchToken({ school_id, studio_id, scene_id });

  // Create S3 client with temporary credentials
  const s3 = new S3Client({
    region: token.region,
    credentials: {
      accessKeyId: token.credentials.accessKeyId,
      secretAccessKey: token.credentials.secretAccessKey,
      sessionToken: token.credentials.sessionToken,
    },
    // Disable request checksum calculation for better upload performance
    requestChecksumCalculation: "WHEN_REQUIRED",
  });

  let index = 0;
  const uploadedFiles = [];

  // Worker function for concurrent uploads
  async function worker() {
    while (index < files.length) {
      const i = index++;
      const file = files[i];

      // Generate unique S3 key with UUID prefix
      const key = `${token.keyPrefix}${crypto.randomUUID()}_${file.name}`;

      try {
        // Create multipart upload
        const upload = new Upload({
          client: s3,
          params: {
            Bucket: token.bucket,
            Key: key,
            Body: file,
            ContentType: file.type || "application/octet-stream",
          },
          queueSize: 8,
          partSize: 5 * 1024 * 1024, // 3MB parts
          leavePartsOnError: false,
        });

        // Track upload progress
        upload.on("httpUploadProgress", (ev) => {
          if (ev.loaded && ev.total) {
            const percentage = Math.floor((ev.loaded / ev.total) * 100);
            onProgress(i, percentage);
          }
        });

        // Wait for upload completion
        const result = await upload.done();

        // Store uploaded file metadata
        const fileMetadata = {
          key: key,
          size: file.size,
          etag: result.ETag || "",
        };
        uploadedFiles.push(fileMetadata);

        // Notify completion
        onFileComplete(i, key);
      } catch (error) {
        console.error(`Failed to upload file ${i} (${file.name}):`, error);
        throw error;
      }
    }
  }

  // Run concurrent workers
  await Promise.all(Array.from({ length: concurrency }, worker));

  return uploadedFiles;
}
